import { IonApp, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route } from 'react-router-dom';
import Page from './pages/Page';
import React from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider
} from "@apollo/client";
import { setContext } from '@apollo/client/link/context';
import { createUploadLink } from 'apollo-upload-client'

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import Login from './pages/Login';
import AuthProvider from './providers/AuthProvider';
import ProtectedRoute from './routing/ProtectedRoute';
import Announcements from './pages/Announcements';
import { offsetLimitPagination } from '@apollo/client/utilities';
import UserManager from './pages/UserManager';
import DevTools from './pages/DevTools';
import DodCopy from './pages/devTools/DodCopy';
import Aca from "./pages/Aca";
import AcaPipeline from "./pages/aca/AcaPipeline";
import AcaViewOpportunity from "./pages/aca/AcaViewOpportunity";
import AcaSearch from "./pages/aca/AcaSearch";
import AcaAdvancedSearch from "./pages/aca/AcaAdvancedSearch";
import AcaViewContact from "./pages/aca/AcaViewContact";
import AcaEditContact from "./pages/aca/AcaEditContact";
import AcaViewContacts from "./pages/aca/AcaViewContacts";
import AcaViewImage from "./pages/aca/AcaViewImage";
import UserManagerRules from "./pages/userManager/UserManagerRules";
import UserManagerRoles from "./pages/userManager/UserManagerRoles";
import "./App.css";
import UserManagerEditUser from "./pages/userManager/UserManagerEditUser";
import UserManagerEditRole from "./pages/userManager/UserManagerEditRole";
import UserManagerEditRule from "./pages/userManager/UserManagerEditRule";
import UserManagerCreateUser from "./pages/userManager/UserManagerCreateUser";
import UserManagerCreateRole from "./pages/userManager/UserManagerCreateRole";
import UserManagerCreateRule from "./pages/userManager/UserManagerCreateRule";
import UserManagerGridMatrix from "./pages/userManager/UserManagerGridMatrix";
import ResetPassword from "./pages/ResetPassword";
import AcaBatchUpdate from "./pages/aca/AcaBatchUpdate";
import ViaSplitPane from "./components/ViaSplitPane";
import AcaSettings from "./pages/aca/AcaSettings";
import NotificationModalProvider from "./providers/NotificationModalProvider";

setupIonicReact();

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
});

const App: React.FC = () => {
    const [client] = React.useState(new ApolloClient({
        link: authLink.concat(httpLink),
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        announcements: offsetLimitPagination()
                    },
                },
            },
        })
    }));

    return (
        <IonApp>
            <ApolloProvider client={client}>
                <NotificationModalProvider>
                    <IonReactRouter>
                        <AuthProvider>
                            <ViaSplitPane>
                                <Route path="/" exact={true}>
                                    <Login/>
                                </Route>
                                <Route path={"/reset"} exact={true}>
                                    <ResetPassword/>
                                </Route>

                                <Route path={"/reset/:token"} exact={true}>
                                    <ResetPassword/>
                                </Route>

                                <Route path="/announcements" exact={true}>
                                    <ProtectedRoute>
                                        <Announcements/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManager/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/edit-user/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerEditUser/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/edit-role/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerEditRole/>
                                    </ProtectedRoute>
                                </Route>

                                <Route path="/user-manager/edit-rule/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerEditRule/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/create-user/" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerCreateUser/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/create-role/" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerCreateRole/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/create-rule/" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerCreateRule/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/rules" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerRules/>
                                    </ProtectedRoute>
                                </Route>

                                <Route path="/user-manager/roles" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerRoles/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/user-manager/grid-matrix" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <UserManagerGridMatrix/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/dev-tools" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAccessDevTools']}>
                                        <DevTools/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/dev-tools/dod-copy" exact={true}>
                                    <ProtectedRoute requiredRules={['CanAdminUsers']}>
                                        <DodCopy/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <Aca/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/pipeline/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaPipeline/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/view-opportunity/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaViewOpportunity/>
                                    </ProtectedRoute>
                                </Route>

                                <Route path="/aca/batch" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaBatch']}>
                                        <AcaBatchUpdate/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/advanced-search" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaAdvancedSearch/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/search/:query" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaSearch/>
                                    </ProtectedRoute>
                                </Route>

                                <Route path="/aca/view-contact/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaViewContact/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/edit-contact/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaEditContact/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/contacts/" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaViewContacts/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/admin/settings" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaOpsAdmin']}>
                                        <AcaSettings/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/aca/view-image/:id" exact={true}>
                                    <ProtectedRoute requiredRules={['AcaView']}>
                                        <AcaViewImage/>
                                    </ProtectedRoute>
                                </Route>
                                <Route path="/page/:name" exact={true}>
                                    <ProtectedRoute>
                                        <Page/>
                                    </ProtectedRoute>
                                </Route>


                            </ViaSplitPane>
                        </AuthProvider>
                    </IonReactRouter>
                </NotificationModalProvider>
            </ApolloProvider>
        </IonApp>
    );
};

export default App;
